/** Matches IdP `ClaimsConstants.SHORTCUTKEY` on role claims (surfaced on the user profile like other role settings). */
export const GLOBAL_ROLE_SHORTCUTS_CLAIM_TYPE = "shortcutkey";

/**
 * Parse JSON stored in the role claim (or API) into normalized shortcut items.
 * @param {string|unknown} raw Claim value or profile field (usually a JSON array string).
 */
export function parseGlobalShortcutsClaimValue(raw) {
    if (raw == null || raw === "") return [];
    if (Array.isArray(raw)) {
        return dedupeShortcuts(raw.map(normalizeShortcutItem).filter(Boolean));
    }
    if (typeof raw === "string") {
        try {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr)) return dedupeShortcuts(arr.map(normalizeShortcutItem).filter(Boolean));
        } catch {
            return [];
        }
    }
    return [];
}

/** Read raw claim from OIDC user profile (claim types may vary in casing). */
export function getGlobalShortcutsRawFromUserProfile(profile) {
    if (!profile || typeof profile !== "object") return null;
    const t = GLOBAL_ROLE_SHORTCUTS_CLAIM_TYPE;
    const v =
        profile[t] ??
        profile[t.toUpperCase()] ??
        profile.SHORTCUTKEY ??
        profile.shortcutkey;
    if (v == null || v === "") return null;
    return v;
}

/** Global shortcuts for the signed-in user from role claims already on the token/profile. */
export function parseGlobalShortcutsFromUserProfile(profile) {
    return parseGlobalShortcutsClaimValue(getGlobalShortcutsRawFromUserProfile(profile));
}

/**
 * Normalizes Shortcut/GetShortcuts API payload (supports legacy string/array and { personal, global }).
 * API may emit PascalCase property names from Newtonsoft.
 */
export function normalizeShortcutItem(item) {
    if (!item || typeof item !== "object") return null;
    const id = item.id ?? item.Id;
    const text = item.text ?? item.Text;
    const path = item.path ?? item.Path;
    const icon = item.icon ?? item.Icon;
    return { ...item, id, text, path, icon };
}

/** First occurrence wins. Same path (case-insensitive, trimmed) = duplicate; without path, same label text = duplicate. */
export function dedupeShortcuts(items) {
    if (!Array.isArray(items) || items.length === 0) return items || [];
    const seen = new Set();
    const out = [];
    for (const item of items) {
        if (!item) continue;
        const pathRaw = item.path != null ? String(item.path).trim() : "";
        const key = pathRaw
            ? `path:${pathRaw.toLowerCase()}`
            : `text:${String(item.text ?? "").trim().toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(item);
    }
    return out;
}

export function parseShortcutsApiResponse(data) {
    let personal = [];
    let global = [];

    if (data == null) {
        return { personal, global };
    }

    if (typeof data === "string") {
        try {
            const parsed = JSON.parse(data);
            return parseShortcutsApiResponse(parsed);
        } catch {
            return { personal, global };
        }
    }

    if (Array.isArray(data)) {
        return { personal: data.map(normalizeShortcutItem).filter(Boolean), global };
    }

    const rawPersonal = data.personal ?? data.Personal;
    const rawGlobal = data.global ?? data.Global;

    if (Array.isArray(rawPersonal)) {
        personal = rawPersonal.map(normalizeShortcutItem).filter(Boolean);
    } else if (typeof rawPersonal === "string") {
        try {
            const arr = JSON.parse(rawPersonal);
            if (Array.isArray(arr)) personal = arr.map(normalizeShortcutItem).filter(Boolean);
        } catch {
            personal = [];
        }
    }

    if (Array.isArray(rawGlobal)) {
        global = dedupeShortcuts(rawGlobal.map(normalizeShortcutItem).filter(Boolean));
    } else if (typeof rawGlobal === "string") {
        try {
            const arr = JSON.parse(rawGlobal);
            if (Array.isArray(arr)) global = dedupeShortcuts(arr.map(normalizeShortcutItem).filter(Boolean));
        } catch {
            global = [];
        }
    }

    return { personal, global };
}
